import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Portal } from "@headlessui/react";
import { cx } from "@jugl-web/utils";
import {
  Fragment,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { TableGridColumn, TableGridProps } from "../types";
import { DraggingColumnHeader } from "./components/DraggingColumnHeader";
import { TableTd as DefaultTableTd } from "./components/TableTd";
import { TableTh } from "./components/TableTh";
import { tableGridClassesByVariant } from "./helpers";
import { useTableGridStoragePreferences } from "./useTableGridStoragePreferences";

export interface TableGridHandle {
  scrollToTop: () => void;
}

const TableGridInner = <TData,>(
  {
    id,
    variant = "row",
    columns,
    data,
    emptyContent,
    classNames,
    style,
    bottomContent,
    inline,
    hasStickyHeader = false,
    hasStickyFirstColumn = false,
    noCellPadding,
    customRows,
    customComponents,
    isItemActive,
    onRowClick,
    onReachEnd,
    onColumnsReorder,
  }: TableGridProps<TData>,
  ref: React.Ref<TableGridHandle>
) => {
  const { ref: bottomAnchorRef, inView: bottomAnchorInView } = useInView();
  const isVariantGrid = variant === "grid";

  const containerRef = useRef<HTMLDivElement>(null);

  const { columnsOrder, columnsWidths, updateColumnWidth, updateColumnsOrder } =
    useTableGridStoragePreferences(id);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      containerRef.current?.scrollTo({
        top: 0,
      });
    },
  }));

  const previousBottomAnchorInViewRef = useRef(bottomAnchorInView);
  useEffect(() => {
    if (bottomAnchorInView && !previousBottomAnchorInViewRef.current) {
      onReachEnd?.();
    }
    previousBottomAnchorInViewRef.current = bottomAnchorInView;
  }, [bottomAnchorInView, onReachEnd]);

  const reachEndAnchorVisible = data.length > 0 && !!onReachEnd;

  const visibleColumnsSorted = useMemo(() => {
    // Filter out hidden columns
    const filteredColumns = columns.filter((col) => !col.isHidden);

    // Sort columns based on order property or internal order state
    return filteredColumns.sort((a, b) => {
      // If both columns have order property, sort by order
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }

      // If only one has order property, prioritize it
      if (a.order !== undefined && b.order === undefined) {
        return -1;
      }
      if (a.order === undefined && b.order !== undefined) {
        return 1;
      }

      // If neither has order property, use internal order state
      const aIndex = columnsOrder.indexOf(a.id);
      const bIndex = columnsOrder.indexOf(b.id);

      // If both are in internal order, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only one is in internal order, prioritize it
      if (aIndex !== -1 && bIndex === -1) {
        return -1;
      }
      if (aIndex === -1 && bIndex !== -1) {
        return 1;
      }

      // If neither is in internal order, maintain original order
      return 0;
    });
  }, [columns, columnsOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [draggedColumn, setDraggedColumn] =
    useState<TableGridColumn<TData> | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const matchingColumn = visibleColumnsSorted.find(
        (column) => column.id === event.active.id
      );

      if (matchingColumn) {
        setDraggedColumn(matchingColumn);
      }
    },
    [visibleColumnsSorted]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggedColumn(null);

      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = visibleColumnsSorted.findIndex(
          (col) => col.id === active.id
        );
        const newIndex = visibleColumnsSorted.findIndex(
          (col) => col.id === over?.id
        );

        const reorderedColumns = arrayMove(
          visibleColumnsSorted,
          oldIndex,
          newIndex
        );
        const newOrder = reorderedColumns
          .filter((col) => !col.isIgnoredWhenSavingOrder)
          .map((col) => col.id);

        // Make sure that any not-draggable (pinned) column hasn't changed its position
        const pinnedColumnIndices = reorderedColumns.reduce<number[]>(
          (acc, col, idx) => {
            if (!col.isDraggable) {
              acc.push(idx);
            }
            return acc;
          },
          []
        );

        const hasPinnedColumnOrderChanged = pinnedColumnIndices.some(
          (pinnedColumnIndex) =>
            columns[pinnedColumnIndex].id !==
            reorderedColumns[pinnedColumnIndex].id
        );

        if (hasPinnedColumnOrderChanged) {
          return;
        }

        updateColumnsOrder(newOrder);
        onColumnsReorder?.(newOrder);
      }
    },
    [visibleColumnsSorted, updateColumnsOrder, onColumnsReorder, columns]
  );

  const handleTdMouseEvent =
    (eventType: "enter" | "leave") => (rowIdx: number) => {
      const rowColumns = document.querySelectorAll(
        `[data-table-id='${id}'][data-table-row='${rowIdx}']`
      );

      rowColumns.forEach((column) => {
        (column as HTMLElement).dataset.rowHovered =
          eventType === "enter" ? "true" : "false";
      });
    };

  // Normalize custom rows list for faster lookup
  const customRowsDict = useMemo(() => {
    if (!customRows) {
      return {};
    }

    return customRows.reduce<Record<number, ReactNode>>((acc, row) => {
      acc[row.afterIndex] = row.content;
      return acc;
    }, {});
  }, [customRows]);

  const gridTemplateRows = useMemo(() => {
    const rows: string[] = ["min-content"]; // Header

    // Build rows array accounting for custom rows
    data.forEach((_, rowIndex) => {
      rows.push("min-content"); // Data row

      // Check if there's a custom row after this index
      const hasCustomRow = rowIndex in customRowsDict;

      if (hasCustomRow) {
        rows.push("auto"); // Custom row
      }
    });

    if (bottomContent) rows.push("auto");
    if (reachEndAnchorVisible) rows.push("auto");

    return rows.length > 1 ? rows.join(" ") : "1fr";
  }, [data, bottomContent, reachEndAnchorVisible, customRowsDict]);

  const gridTemplateColumns = useMemo(
    () =>
      visibleColumnsSorted
        .map((item, index) => {
          if (item.resizeOptions?.isEnabled && columnsWidths[item.id]) {
            return `${columnsWidths[item.id]}px`;
          }

          if (item.grow) {
            return "auto";
          }

          if (item.width) {
            return `${item.width}px`;
          }

          const hasAnyGrowColumn = visibleColumnsSorted.some(
            (column) => column.grow
          );

          const isLastColumn = index === visibleColumnsSorted.length - 1;

          return !hasAnyGrowColumn && isLastColumn ? "auto" : `min-content`;
        })
        .join(" "),
    [visibleColumnsSorted, columnsWidths]
  );

  const finalClassWrapper = useMemo(
    () =>
      cx(
        "jugl__custom-scrollbar w-full grid rounded-xl overflow-x-auto border-[1px] border-solid border-[#EEF2F5]",
        { "h-full overflow-auto": !inline && !isVariantGrid },
        tableGridClassesByVariant[variant].wrapper,
        classNames?.wrapper
      ),
    [inline, isVariantGrid, variant, classNames?.wrapper]
  );

  const stickyColumnClassName = "sticky! left-0 top-0 z-10 bg-white";
  const stickyRowClassName = "sticky! top-0 z-10 bg-white";

  const TableTdComponent = customComponents?.TableTd || DefaultTableTd;

  return (
    <div
      ref={containerRef}
      id={id}
      className={finalClassWrapper}
      style={{
        contain: "paint",
        gridTemplateRows,
        gridTemplateColumns,
        ...style,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {visibleColumnsSorted.map((col, idx) => (
          <TableTh
            key={col.id}
            data-table-column={idx}
            data-table-column-reversed={visibleColumnsSorted.length - idx - 1}
            id={col.id}
            idx={idx}
            col={col}
            className={cx(
              tableGridClassesByVariant[variant].headerCell,
              hasStickyFirstColumn && idx === 0 && stickyColumnClassName,
              hasStickyHeader && stickyRowClassName,
              classNames?.headerCell,
              col.headerClassName
            )}
            style={col.headerStyle}
            onColumnResize={updateColumnWidth}
            currentWidth={columnsWidths[col.id] || col.width}
          />
        ))}
        {data.map((item, rowIdx) => {
          const hasCustomRow = rowIdx in customRowsDict;

          return (
            <Fragment key={+rowIdx}>
              {visibleColumnsSorted.map((col, idx) => (
                <TableTdComponent
                  key={`${+rowIdx}-${col.id}`}
                  col={col}
                  item={item}
                  colIdx={idx}
                  rowIdx={rowIdx}
                  className={cx(
                    tableGridClassesByVariant[variant].bodyCell,
                    hasStickyFirstColumn && idx === 0 && stickyColumnClassName,
                    !isVariantGrid && idx === 0 && "pl-8",
                    !isVariantGrid &&
                      idx === visibleColumnsSorted.length - 1 &&
                      "pr-8",
                    !isVariantGrid &&
                      rowIdx !== data.length - 1 &&
                      "border-x-0 border-t-0 border-b-2 border-solid border-gray-50",
                    noCellPadding && "px-0 py-0",
                    isItemActive?.(item) && "bg-grey-100",
                    classNames?.bodyCell,
                    col.className
                  )}
                  style={col.style}
                  onClick={() => {
                    col.onCellClick?.(item, rowIdx);
                    onRowClick?.(item);
                  }}
                  onMouseEnter={() => handleTdMouseEvent("enter")(rowIdx)}
                  onMouseLeave={() => handleTdMouseEvent("leave")(rowIdx)}
                  data-table-id={id}
                  data-table-row-reversed={data.length - rowIdx - 1}
                  data-table-row={rowIdx}
                  data-table-column={idx}
                  data-table-column-reversed={
                    visibleColumnsSorted.length - idx - 1
                  }
                />
              ))}
              {hasCustomRow && (
                <div
                  style={{
                    gridColumn: `span ${visibleColumnsSorted.length} / span ${visibleColumnsSorted.length}`,
                  }}
                >
                  {customRowsDict[rowIdx]}
                </div>
              )}
            </Fragment>
          );
        })}
        {data.length === 0 && emptyContent ? (
          <div
            style={{
              gridColumn: `span ${visibleColumnsSorted.length} / span ${visibleColumnsSorted.length}`,
            }}
          >
            {emptyContent}
          </div>
        ) : null}
        {reachEndAnchorVisible && (
          <div
            ref={bottomAnchorRef}
            style={{
              gridColumn: `span ${visibleColumnsSorted.length} / span ${visibleColumnsSorted.length}`,
            }}
            className="-mt-px h-px"
          />
        )}
        {bottomContent ? (
          <div
            style={{
              gridColumn: `span ${visibleColumnsSorted.length} / span ${visibleColumnsSorted.length}`,
            }}
          >
            {bottomContent}
          </div>
        ) : null}
        <Portal>
          <DragOverlay dropAnimation={null}>
            {draggedColumn && (
              <DraggingColumnHeader label={draggedColumn.title} />
            )}
          </DragOverlay>
        </Portal>
      </DndContext>
    </div>
  );
};

export const TableGrid = forwardRef(TableGridInner) as <TData>(
  props: TableGridProps<TData> & { ref?: React.Ref<TableGridHandle> }
) => ReturnType<typeof TableGridInner>;
